import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';


import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import useTimetableTypes from '../../stateManagers/hooks/useTimetableTypes';
import useClassesStore from '../../stateManagers/store/useClassesStore';
import DeleteModal from './parts/DeleteModal';
import TimetableTypeChips from './parts/TimetableTypeChips';
import DayChips from './parts/DayChips';
import AddTimetableEntryModal from './parts/AddTimetableEntryModal';
import ViewTimeSlotsModal from './parts/ViewTimeSlotsModal';
import ManageTimetableTypesModal from './parts/ManageTimetableTypesModal';
import CloneTimetableModal from './parts/CloneTimetableModal';
import {
  detectPeriodTeacherConflicts,
  conflictCellKey,
  CONFLICT_BG,
  CONFLICT_BG_HOVER,
} from './parts/TimetablePeriodConflicts';
import { formatSlotTypeLabel, hasNoSubject } from './parts/subparts/timetableSlotTypes';
import { showDevTools } from '../../config/env';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
// Shared types — single source of truth. Do NOT redeclare TimetableItem/DayOfWeek locally.
// Backend IDs (class_id, subject_id, teacher_id, entry id) are Mongo ObjectID hex strings.
import { TimetableItem, DayOfWeek, DayFilter } from './parts/subparts/types';

interface ClassTimetable {
  class_id: string;
  class_name: string;
  schedule: TimetableItem[];
}

interface TableRow {
  period_label: string;
  start_time: string;
  end_time: string;
  time_range: string;
  [key: string]: any;
}

interface SelectedCell {
  classId: string;
  section: string | null;
  period: string;
  scheduleItems: TimetableItem[];
  className: string;
}

const shortDays: Record<DayOfWeek, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
};

const Timetable: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [timetableData, setTimetableData] = useState<ClassTimetable[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTimetableTypeId, setSelectedTimetableTypeId] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<DayFilter>('Monday');
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [timeSlotsModalOpen, setTimeSlotsModalOpen] = useState(false);
  const [manageTypesModalOpen, setManageTypesModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [addModalContext, setAddModalContext] = useState<{
    timetableTypeId: string;
    classId: string;
    section: string | null;
    periodLabel: string;
    day: DayFilter;
    existingItems: TimetableItem[];
  } | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);

  const timetableTypes = useTimetableTypes(false);
  const { classOptions, fetchClasses, isFetching: isFetchingClasses, hasFetched } = useClassesStore();

  // ── Session context (required by GET /api/timetables-by-type) ──────
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const sessionId = schoolDetails?.current_session?.id ?? null;


  useEffect(() => {
    if (!hasFetched && !isFetchingClasses) {
      fetchClasses(false);
    }
  }, [hasFetched, isFetchingClasses, fetchClasses]);

  useEffect(() => {
    if (timetableTypes.length > 0 && !selectedTimetableTypeId) {
      setSelectedTimetableTypeId(timetableTypes[0].id);
    }
  }, [timetableTypes]);

  const selectedTimetableType = timetableTypes.find((t) => t.id === selectedTimetableTypeId);

  const fetchTimetableData = async () => {
    if (!selectedTimetableTypeId) {
      setTimetableData([]);
      return;
    }

    if (!sessionId) {
      // Session not resolved yet — don't fire the request, avoid a guaranteed 400.
      setTimetableData([]);
      return;
    }

    setLoading(true);
    try {
      // IDs are Mongo ObjectID hex strings — do NOT parseInt() them.
      const response = await apiClient.get('/api/timetables-by-type', {
        params: {
          timetable_type_id: selectedTimetableTypeId,
          session_id: sessionId,
        },
      });

      if (response.data.status === 'success') {
        setTimetableData(response.data.data.classes || []);
      } else {
        throw new Error(response.data.message || 'Failed to load timetable');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch timetable';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 5000 });
      setTimetableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimetableData();
  }, [selectedTimetableTypeId, sessionId]);

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimeRange = (startTime: string, endTime: string): string => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const parseDaysOfWeek = (days: string[] | string | null | undefined): DayOfWeek[] => {
    // Backend sends day_of_week as a real array. Kept the string/JSON fallback
    // in case any older cached response or endpoint still stringifies it.
    if (Array.isArray(days)) return days as DayOfWeek[];
    if (!days) return [];
    try {
      const parsed = JSON.parse(days);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const isScheduledForDay = (item: TimetableItem, day: DayFilter): boolean => {
    if (!item.day_of_week) return false;
    const days = parseDaysOfWeek(item.day_of_week);
    if (day === 'All') return days.length > 0;
    return days.includes(day);
  };

  const findScheduleItems = (
    classId: string,
    section: string | null,
    periodLabel: string,
    day: DayFilter
  ): TimetableItem[] => {
    const classData = timetableData.find((c) => c.class_id === classId);
    if (!classData) return [];

    const potentialItems = classData.schedule.filter(
      (s) => s.section === section && s.period_label === periodLabel
    );

    return potentialItems.filter((item) => isScheduledForDay(item, day));
  };

  const getDaysText = (items: TimetableItem[], showForSingleSubject: boolean = true): string | null => {
    if (items.length > 1) {
      // Show teacher name with days for each entry
      return items.map(item => {
        const days = parseDaysOfWeek(item.day_of_week);
        const daysStr = days.map(d => shortDays[d]).join(', ');
        return `${item.teacher_name} (${daysStr})`;
      }).join(' / ');
    }
    if (items.length === 1 && showForSingleSubject) {
      const days = parseDaysOfWeek(items[0].day_of_week);
      if (days.length > 1) {
        return `(${days.map((d) => shortDays[d]).join(', ')})`;
      }
    }
    return null;
  };

  const allDaysOrder: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const isAllDays = selectedDay === 'All';

  // "Mon–Wed, Fri" style label; all six days -> "Daily"
  const formatDaysCompact = (days: DayOfWeek[]): string => {
    const sorted = allDaysOrder.filter((d) => days.includes(d));
    if (sorted.length === 0) return '';
    if (sorted.length === allDaysOrder.length) return 'Daily';
    const runs: string[] = [];
    let i = 0;
    while (i < sorted.length) {
      let j = i;
      while (j + 1 < sorted.length && allDaysOrder.indexOf(sorted[j + 1]) === allDaysOrder.indexOf(sorted[j]) + 1) j++;
      runs.push(
        j - i >= 2
          ? `${shortDays[sorted[i]]}–${shortDays[sorted[j]]}`
          : sorted.slice(i, j + 1).map((d) => shortDays[d]).join(', ')
      );
      i = j + 1;
    }
    return runs.join(', ');
  };

  // Cell body used when the "All" day chip is selected: one block per entry, each tagged with its days.
  const renderAllDaysItems = (items: TimetableItem[], isConflict: boolean) =>
    items.map((item, idx) => {
      const itemIsBreak = item.slot_type === 'break';
      return (
        <div
          key={item.id || idx}
          style={{
            width: '100%',
            textAlign: 'center',
            paddingTop: idx > 0 ? 6 : 0,
            borderTop: idx > 0 ? '1px dashed #cbd5e1' : 'none',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.3, color: itemIsBreak ? '#b45309' : '#1e40af' }}>
            {hasNoSubject(item.slot_type) ? formatSlotTypeLabel(item.slot_type) : item.subject_name}
          </div>
          {item.teacher_name && (
            <div style={{ fontSize: 10.5, color: '#64748b' }}>
              {item.teacher_name}
              {isConflict && <span style={{ color: '#ef4444', fontSize: 12 }}> ●</span>}
            </div>
          )}
          <span
            style={{
              display: 'inline-block',
              marginTop: 3,
              padding: '1px 8px',
              borderRadius: 20,
              fontSize: 10,
              fontWeight: 700,
              background: '#e0e7ff',
              color: '#3730a3',
            }}
          >
            {formatDaysCompact(parseDaysOfWeek(item.day_of_week))}
          </span>
        </div>
      );
    });

  const allSchedules = timetableData.flatMap((cls) => cls.schedule);
  const periodMap = new Map<string, { startTime: string; endTime: string }>();

  allSchedules.forEach((item) => {
    if (!periodMap.has(item.period_label)) {
      periodMap.set(item.period_label, {
        startTime: item.start_time,
        endTime: item.end_time,
      });
    }
  });

  const uniquePeriods = Array.from(periodMap.keys()).sort();

  const classSections = timetableData.flatMap((cls) => {
    const sections = Array.from(new Set(cls.schedule.map((item) => item.section)))
      .filter((s): s is string => s !== null && s !== '')
      .sort();

    if (sections.length === 0) sections.push('');

    return sections.map((section) => ({
      class_id: cls.class_id,
      class_name: cls.class_name,
      section: section,
      display_name: `${cls.class_name}${section ? ` ${section}` : ''}`,
    }));
  });

  const tableData: TableRow[] = uniquePeriods.map((period) => {
    const times = periodMap.get(period)!;
    const row: TableRow = {
      period_label: period,
      start_time: times.startTime,
      end_time: times.endTime,
      time_range: formatTimeRange(times.startTime, times.endTime),
    };

    classSections.forEach((cs) => {
      const key = `class_${cs.class_id}_${cs.section || 'nosection'}`;
      row[key] = findScheduleItems(cs.class_id, cs.section, period, selectedDay);
    });

    return row;
  });

  const flippedTableData: TableRow[] = classSections.map((cs) => {
    const row: TableRow = {
      period_label: cs.display_name,
      start_time: '',
      end_time: '',
      time_range: cs.display_name,
      class_id: cs.class_id,
      section: cs.section,
    };

    uniquePeriods.forEach((period) => {
      const key = `period_${period}`;
      row[key] = findScheduleItems(cs.class_id, cs.section, period, selectedDay);
    });

    return row;
  });

  const getTeacherOccurrences = (): Map<string, number> => {
    const teacherCount = new Map<string, number>();
    timetableData.forEach((cls) => {
      cls.schedule.forEach((item) => {
        if (item.teacher_name && item.slot_type !== 'break' && isScheduledForDay(item, selectedDay)) {
          const currentCount = teacherCount.get(item.teacher_name) || 0;
          teacherCount.set(item.teacher_name, currentCount + 1);
        }
      });
    });
    return teacherCount;
  };

  const teacherOccurrences = getTeacherOccurrences();

  // Real double-bookings only: same teacher, same period, same day, different
  // class/section. (teacherOccurrences above counts a teacher across the whole
  // day, which is normal and not a conflict on its own.)
  const periodConflicts: Set<string> = (() => {
    if (!isAllDays) {
      return detectPeriodTeacherConflicts(
        classSections,
        uniquePeriods,
        (classId, section, period) => findScheduleItems(classId, section, period, selectedDay),
      );
    }
    // "All": a clash on ANY day marks the cell (different days never clash with each other).
    const union = new Set<string>();
    allDaysOrder.forEach((day) => {
      detectPeriodTeacherConflicts(
        classSections,
        uniquePeriods,
        (classId, section, period) => findScheduleItems(classId, section, period, day),
      ).forEach((k) => union.add(k));
    });
    return union;
  })();

  const getTeacherBackgroundColor = (teacherName: string): string => {
    const count = teacherOccurrences.get(teacherName) || 0;
    if (count <= 1) return 'transparent';

    const colors = [
      '#fffcf0',
      '#f0f8fa',
      '#f2faf4',
      '#fdf5f5',
      '#f6f7f8',
      '#fff8e7',
      '#fdebe8',
      '#e8e6ff',
    ];

    let hash = 0;
    for (let i = 0; i < teacherName.length; i++) {
      hash = teacherName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };

  const handlePrint = () => {
    const printContent = document.getElementById('timetable-print-area');
    if (!printContent) {
      toast.current?.show({ severity: 'warn', summary: 'Error', detail: 'Print area not found.' });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.current?.show({ severity: 'warn', summary: 'Blocked', detail: 'Popup blocked. Allow popups to print.' });
      return;
    }

    const title = `${selectedTimetableType?.label || 'Class Timetable'} - ${isAllDays ? 'All Days' : selectedDay}`;

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 30px; color: #000; background: #fff; }
          h1 { text-align: center; color: #1e40af; font-size: 28px; margin: 0 0 8px 0; font-weight: 700; }
          h2 { text-align: center; color: #444; font-size: 16px; margin: 0 0 30px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #000; padding: 12px; text-align: center; vertical-align: middle; background-color: #fff; }
          th { color: #1e40af; font-weight: 800; }
          th strong { font-size: 14px; color: #1e40af; }
          th small { display: block; font-weight: 600; color: #1e40af; margin-top: 4px; font-size: 11px; }
          .class-header { background-color: #fff; font-weight: 700; font-size: 13px; }
          .class-header strong { color: #1e40af; }
          .break-cell { background-color: #fff; color: #000; font-weight: 600; }
          .conflict-cell { background-color: #fff; }
          .subject { font-weight: 700; font-size: 13px; color: #1e40af; margin-bottom: 4px; }
          .teacher { font-weight: 700; font-size: 12px; color: #000; }
          .days { font-weight: 700; font-size: 11px; color: #000; margin-top: 3px; }
          .dot { color: #000; margin-left: 4px; font-weight: bold; }
          .empty { color: #000; font-style: italic; }
          @media print { 
            @page { size: A4 landscape; margin: 1cm; } 
            body { padding: 0; }
          }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 600);
  };

  const periodColumnTemplate = (rowData: TableRow) => {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '16px 12px',
          background: '#f8fafc',
          minHeight: '100px',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '4px' }}>
          {rowData.period_label}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '500' }}>{rowData.time_range}</div>
      </div>
    );
  };

  const classColumnTemplate = (classId: string, section: string | null) => {
    return (rowData: TableRow) => {
      const key = `class_${classId}_${section || 'nosection'}`;
      const items: TimetableItem[] = rowData[key] || [];
      const isBreak = items.some((item) => item.slot_type === 'break');
      const hasMultiple = !isAllDays && items.length > 1;
      const isConflict = periodConflicts.has(conflictCellKey(rowData.period_label, classId, section));
      const hasRepeatingTeacher = !isAllDays && items.some((item) => (teacherOccurrences.get(item.teacher_name) || 0) > 1);

      let backgroundColor = 'transparent';
      if (isBreak) backgroundColor = '#fef3c7';
      else if (isConflict) backgroundColor = CONFLICT_BG;
      else if (hasMultiple) backgroundColor = '#fef3c7';
      else if (items.length === 1 && hasRepeatingTeacher)
        backgroundColor = getTeacherBackgroundColor(items[0].teacher_name);
      else if (items.length > 0) backgroundColor = 'rgba(239, 246, 255, 0.6)';

      return (
        <div
          onClick={() => {
            const classInfo = classSections.find((cs) => cs.class_id === classId && cs.section === section);
            setSelectedCell({
              classId,
              section,
              period: rowData.period_label,
              scheduleItems: items,
              className: classInfo?.display_name || '',
            });
            if (items.length > 0) {
              setDeleteDialogVisible(true);
            } else {
              setAddModalContext({
                timetableTypeId: selectedTimetableTypeId,
                classId,
                section,
                periodLabel: rowData.period_label,
                day: selectedDay,
                existingItems: items,
              });
              setAddModalOpen(true);
            }
          }}
          style={{
            padding: '16px 12px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            background: backgroundColor,
            minHeight: '100px',
          height: '100%',
          boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isConflict
              ? CONFLICT_BG_HOVER
              : isBreak || hasMultiple
                ? '#fde68a'
                : '#e0f2fe';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = backgroundColor;
          }}
        >
          {items.length > 0 && isAllDays ? (
            renderAllDaysItems(items, isConflict)
          ) : items.length > 0 ? (
            <>
              <div
                style={{
                  fontWeight: '600',
                  fontSize: items.length > 1 ? '11px' : '13px',
                  color: isBreak ? '#b45309' : '#1e40af',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {items.map((i) =>
                  hasNoSubject(i.slot_type) ? formatSlotTypeLabel(i.slot_type) : i.subject_name
                ).join(' / ')}
              </div>
              {!hasNoSubject(items[0]?.slot_type) || items.some((i) => i.teacher_name) ? (
                items.some((i) => i.teacher_name) && (
                  <div
                    style={{
                      fontSize: items.length > 1 ? '10px' : '11px',
                      color: '#64748b',
                      textAlign: 'center',
                    }}
                  >
                    {items.map((i) => i.teacher_name).filter(Boolean).join(' / ')}
                    {(isConflict || hasMultiple) && (
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>●</span>
                    )}
                  </div>
                )
              ) : null}
            </>
          ) : (
            <span style={{ fontSize: '12px', color: '#cbd5e1', fontWeight: '500' }}>—</span>
          )}
        </div>
      );
    };
  };

  const classNameColumnTemplate = (rowData: TableRow) => {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '16px 12px',
          background: '#f8fafc',
          minHeight: '100px',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>{rowData.time_range}</div>
      </div>
    );
  };

  const flippedPeriodColumnTemplate = (period: string) => {
    return (rowData: TableRow) => {
      const key = `period_${period}`;
      const items: TimetableItem[] = rowData[key] || [];
      const isBreak = items.some((item) => item.slot_type === 'break');
      const hasMultiple = !isAllDays && items.length > 1;
      const isConflict = periodConflicts.has(conflictCellKey(period, rowData.class_id, rowData.section));
      const hasRepeatingTeacher = !isAllDays && items.some((item) => (teacherOccurrences.get(item.teacher_name) || 0) > 1);

      let backgroundColor = 'transparent';
      if (isBreak) backgroundColor = '#fef3c7';
      else if (isConflict) backgroundColor = CONFLICT_BG;
      else if (hasMultiple) backgroundColor = '#fef3c7';
      else if (items.length === 1 && hasRepeatingTeacher)
        backgroundColor = getTeacherBackgroundColor(items[0].teacher_name);
      else if (items.length > 0) backgroundColor = 'rgba(239, 246, 255, 0.6)';

      return (
        <div
          onClick={() => {
            setSelectedCell({
              classId: rowData.class_id,
              section: rowData.section,
              period: period,
              scheduleItems: items,
              className: rowData.time_range,
            });
            if (items.length > 0) {
              setDeleteDialogVisible(true);
            } else {
              setAddModalContext({
                timetableTypeId: selectedTimetableTypeId,
                classId: rowData.class_id,
                section: rowData.section,
                periodLabel: period,
                day: selectedDay,
                existingItems: items,
              });
              setAddModalOpen(true);
            }
          }}
          style={{
            padding: '16px 12px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            background: backgroundColor,
            minHeight: '100px',
          height: '100%',
          boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isConflict
              ? CONFLICT_BG_HOVER
              : isBreak || hasMultiple
                ? '#fde68a'
                : '#e0f2fe';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = backgroundColor;
          }}
        >
          {items.length > 0 && isAllDays ? (
            renderAllDaysItems(items, isConflict)
          ) : items.length > 0 ? (
            <>
              <div
                style={{
                  fontWeight: '600',
                  fontSize: items.length > 1 ? '11px' : '13px',
                  color: isBreak ? '#b45309' : '#1e40af',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {items.map((i) =>
                  hasNoSubject(i.slot_type) ? formatSlotTypeLabel(i.slot_type) : i.subject_name
                ).join(' / ')}
              </div>
              {!hasNoSubject(items[0]?.slot_type) || items.some((i) => i.teacher_name) ? (
                items.some((i) => i.teacher_name) && (
                  <div
                    style={{
                      fontSize: items.length > 1 ? '10px' : '11px',
                      color: '#64748b',
                      textAlign: 'center',
                    }}
                  >
                    {items.map((i) => i.teacher_name).filter(Boolean).join(' / ')}
                    {(isConflict || hasMultiple) && (
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>●</span>
                    )}
                  </div>
                )
              ) : null}
            </>
          ) : (
            <span style={{ fontSize: '12px', color: '#cbd5e1', fontWeight: '500' }}>—</span>
          )}
        </div>
      );
    };
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <DeleteModal
        deleteDialogVisible={deleteDialogVisible}
        setDeleteDialogVisible={setDeleteDialogVisible}
        selectedCell={selectedCell}
        setSelectedCell={setSelectedCell}
        fetchTimetableData={fetchTimetableData}
        toast={toast}
      />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: '100%', overflowX: 'hidden' }}>
        <style>{`
          .p-datatable-thead > tr > th {
            background: #f9fafb !important;
            color: #6b7280 !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            padding: 16px 12px !important;
            border: 1px solid #e5e7eb !important;
            text-align: center !important;
            white-space: nowrap !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 2 !important;
          }
          .p-datatable .p-column-header-content{
            justify-content: center;
          }
            
          .p-datatable-thead > tr > th.p-frozen-column {
            z-index: 3 !important;
          }
          .p-datatable-tbody > tr > td {
            padding: 0 !important;
            border: 1px solid #e5e7eb !important;
            vertical-align: stretch !important;
            height: 1px !important; /* lets cell content use height:100% so backgrounds fill the whole cell */
          }
          .p-datatable-wrapper {
            overflow: auto !important;
          }
          .p-datatable-wrapper::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .p-datatable-wrapper::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .p-datatable-frozen-column {
            position: sticky !important;
            left: 0 !important;
            z-index: 1 !important;
            background: white !important;
          }
          .p-datatable-tbody > tr:hover .p-datatable-frozen-column {
            background: white !important;
          }
          .p-datatable-tbody > tr .p-datatable-frozen-column > div {
            background: #f8fafc !important;
          }
          .p-datatable-tbody > tr:hover .p-datatable-frozen-column > div {
            background: #f8fafc !important;
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @media (max-width: 768px) {
            .header-content {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 16px !important;
            }
            .header-buttons {
              width: 100%;
              justify-content: stretch !important;
            }
            .header-buttons button {
              flex: 1;
            }
          }
        `}</style>

        <GlobalHeaderTitle
          title="Class Timetable"
          subtitle={
            selectedTimetableType
              ? `${selectedTimetableType.label} • ${isAllDays ? 'All days' : selectedDay} • ${uniquePeriods.length} periods • ${classSections.length} classes`
              : 'Select timetable type to view schedule'
          }
          icon="pi pi-calendar"
          buttons={[
            ...(showDevTools
              ? [
                  {
                    label: 'View Timeslots',
                    icon: 'pi pi-clock',
                    onClick: () => setTimeSlotsModalOpen(true),
                    outlined: true,
                    disabled: !selectedTimetableTypeId,
                  },
                  {
                    label: 'Manage Types',
                    icon: 'pi pi-calendar',
                    onClick: () => setManageTypesModalOpen(true),
                    outlined: true,
                  },
                ]
              : []),
            {
              icon: 'pi pi-sync',
              onClick: () => setIsFlipped(!isFlipped),
              outlined: true,
              disabled: !selectedTimetableTypeId || loading || timetableData.length === 0,
            },
            {
              icon: 'pi pi-print',
              onClick: handlePrint,
              outlined: true,
              disabled: !selectedTimetableTypeId || loading || timetableData.length === 0,
            },
            {
              label: 'Clone',
              icon: 'pi pi-copy',
              onClick: () => setCloneModalOpen(true),
              outlined: true,
              disabled: !selectedTimetableTypeId || loading,
            },
            {
              label: 'Add Entry',
              icon: 'pi pi-plus',
              onClick: () => {
                setAddModalContext(null);
                setAddModalOpen(true);
              },
              disabled: !selectedTimetableTypeId,
            },
          ]}
        />

        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1e40af',
              marginBottom: '10px',
              letterSpacing: '0.02em',
            }}
          >
            Select Timetable Type
          </label>

          <TimetableTypeChips
            timetableTypes={timetableTypes}
            selectedTimetableTypeId={selectedTimetableTypeId}
            onSelect={setSelectedTimetableTypeId}
          />
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1e40af',
              marginBottom: '10px',
              letterSpacing: '0.02em',
            }}
          >
            Select Day
          </label>

          <DayChips selectedDay={selectedDay} onSelect={setSelectedDay} />
        </div>

        <div
          style={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {isFetchingClasses || loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', minHeight: '400px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              >
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
                Loading Timetable...
              </h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                Please wait while we fetch the schedule
              </p>
            </div>
          ) : !selectedTimetableTypeId ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', minHeight: '400px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                }}
              >
                <i className="pi pi-filter" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: '700', marginBottom: '8px', textAlign: 'center' }}>
                No Timetable Type Selected
              </h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500', textAlign: 'center', maxWidth: '400px', lineHeight: 1.6 }}>
                Please select a timetable type from the filter above to view the schedule
              </p>
            </div>
          ) : classSections.length === 0 || uniquePeriods.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', minHeight: '400px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
                }}
              >
                <i className="pi pi-inbox" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: '700', marginBottom: '8px', textAlign: 'center' }}>
                No Timetable Available
              </h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: '500', textAlign: 'center', maxWidth: '400px', lineHeight: 1.6 }}>
                No timetable entries found for {isAllDays ? 'any day' : selectedDay}. Click "Add Entry" to create a new entry.
              </p>
            </div>
          ) : (
            <>
              {!isFlipped ? (
                <DataTable value={tableData} loading={false} showGridlines scrollable tableStyle={{ minWidth: '50rem' }}>
                  <Column field="period_label" header="Period" body={periodColumnTemplate} style={{ minWidth: '150px' }} frozen alignFrozen="left" />
                  {classSections.map((cs) => (
                    <Column
                      key={`${cs.class_id}_${cs.section || 'nosection'}`}
                      header={cs.display_name}
                      body={classColumnTemplate(cs.class_id, cs.section)}
                      style={{ minWidth: '180px' }}
                    />
                  ))}
                </DataTable>
              ) : (
                <DataTable value={flippedTableData} loading={false} showGridlines scrollable scrollHeight="calc(100vh - 120px)" tableStyle={{ minWidth: '50rem' }}>
                  <Column field="time_range" header="Class" body={classNameColumnTemplate} style={{ minWidth: '150px' }} frozen alignFrozen="left" />
                  {uniquePeriods.map((period) => {
                    const times = periodMap.get(period)!;
                    return (
                      <Column
                        key={period}
                        header={
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '700', marginBottom: '4px' }}>{period}</div>
                            <div style={{ fontSize: '10px', fontWeight: '500', color: '#9ca3af' }}>
                              {formatTimeRange(times.startTime, times.endTime)}
                            </div>
                          </div>
                        }
                        body={flippedPeriodColumnTemplate(period)}
                        style={{ minWidth: '180px' }}
                      />
                    );
                  })}
                </DataTable>
              )}
            </>
          )}
        </div>

        <div id="timetable-print-area" style={{ display: 'none' }}>
          <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif', background: '#fff', color: '#000' }}>
            <h1 style={{ textAlign: 'center', color: '#1e40af', fontSize: '28px', margin: '0 0 8px 0', fontWeight: '700' }}>
              {selectedTimetableType?.label || 'Class Timetable'}
            </h1>
            <h2 style={{ textAlign: 'center', color: '#444', fontSize: '16px', margin: '0 0 30px 0' }}>
              Generated on: {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
            </h2>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#fff' }}>
                  <th style={{ padding: '12px', color: '#1e40af', border: '1px solid #000', textAlign: 'center', fontWeight: '800', backgroundColor: '#fff' }}>
                    <strong style={{ color: '#1e40af' }}>Class</strong>
                  </th>
                  {uniquePeriods.map((period) => {
                    const times = periodMap.get(period)!;
                    return (
                      <th key={period} style={{ padding: '12px', color: '#1e40af', border: '1px solid #000', textAlign: 'center', minWidth: '140px', backgroundColor: '#fff' }}>
                        <strong style={{ fontSize: '14px', color: '#1e40af' }}>{period}</strong>
                        <small style={{ display: 'block', fontWeight: '600', color: '#1e40af', marginTop: '4px', fontSize: '11px' }}>
                          {formatTimeRange(times.startTime, times.endTime)}
                        </small>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {classSections.map((cs) => (
                  <tr key={`${cs.class_id}_${cs.section || 'nosection'}`}>
                    <td className="class-header" style={{ padding: '14px', backgroundColor: '#fff', fontWeight: '700', border: '1px solid #000', textAlign: 'center', fontSize: '13px' }}>
                      <strong style={{ color: '#1e40af' }}>{cs.display_name}</strong>
                    </td>
                    {uniquePeriods.map((period) => {
                      const items = findScheduleItems(cs.class_id, cs.section, period, selectedDay);
                      const isBreak = items.some((i) => i.slot_type === 'break');
                      const hasConflict = isAllDays
                        ? periodConflicts.has(conflictCellKey(period, cs.class_id, cs.section))
                        : items.length > 1 || items.some((i) => (teacherOccurrences.get(i.teacher_name) || 0) > 1);
                      const daysText = getDaysText(items, isAllDays);

                      return (
                        <td
                          key={period}
                          style={{
                            padding: '12px',
                            border: '1px solid #000',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            minHeight: '80px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {items.length > 0 ? (
                            <>
                              {items.length > 1 ? (
                                <>
                                  <div className="subject" style={{ fontWeight: '700', fontSize: '13px', color: '#1e40af', marginBottom: '6px', lineHeight: '1.4' }}>
                                    {items.map((i) => hasNoSubject(i.slot_type) ? formatSlotTypeLabel(i.slot_type) : i.subject_name).join(' / ')}
                                  </div>
                                  <div style={{ fontSize: '11px', lineHeight: '1.6', color: '#000', fontWeight: '600' }}>
                                    {daysText}
                                    {hasConflict && <span className="dot" style={{ color: '#000', marginLeft: '4px', fontWeight: 'bold' }}>●</span>}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="subject" style={{ fontWeight: '700', fontSize: '13px', color: isBreak ? '#000' : '#1e40af', marginBottom: '4px' }}>
                                    {items.map((i) => hasNoSubject(i.slot_type) ? formatSlotTypeLabel(i.slot_type) : i.subject_name).join(' / ')}
                                  </div>
                                  {items.some((i) => i.teacher_name) && (
                                    <div className="teacher" style={{ fontWeight: '700', fontSize: '12px', color: '#000' }}>
                                      {items.map((i) => i.teacher_name).filter(Boolean).join(' / ')}
                                      {hasConflict && <span className="dot">●</span>}
                                    </div>
                                  )}
                                  {isAllDays && daysText && (
                                    <div className="days">{daysText}</div>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <span className="empty">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '40px', textAlign: 'center', paddingTop: '20px', borderTop: '2px solid #1e40af' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#1e40af', fontWeight: '700', letterSpacing: '0.5px' }}>
                🎓 Skoolpillar Management System | Generated with Excellence ✨
              </p>
            </div>
          </div>
        </div>
      </div>
      <AddTimetableEntryModal
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setAddModalContext(null);
        }}
        onSuccess={fetchTimetableData}
        context={addModalContext}
      />
      <ViewTimeSlotsModal
        open={timeSlotsModalOpen}
        onClose={() => setTimeSlotsModalOpen(false)}
        timetableTypeId={selectedTimetableTypeId}
        timetableTypeName={selectedTimetableType?.label}
        onTimeSlotsChanged={fetchTimetableData}
      />
      <ManageTimetableTypesModal
        open={manageTypesModalOpen}
        onClose={() => setManageTypesModalOpen(false)}
        onChanged={fetchTimetableData}
      />
      <CloneTimetableModal
        open={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        onSuccess={fetchTimetableData}
        sourceTimetableTypeId={selectedTimetableTypeId}
      />
    </>
  );
};

export default Timetable;